import React, { useState } from "react";
import { toast, Toaster } from "sonner";
import { ArrowRight, Phone } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const ROLE_OPTIONS = [
  "Support Coordinator",
  "Case Manager",
  "Parent / Family Member",
  "Guardian",
  "Self-Referral",
  "Other",
];

const STEPS = [
  {
    n: "01",
    title: "Submit the referral",
    body: "Share the basic information we need to get started.",
  },
  {
    n: "02",
    title: "Our intake team reviews it",
    body: "We'll review the referral and determine the appropriate next steps.",
  },
  {
    n: "03",
    title: "We'll connect with you",
    body: "A member of our team will follow up to discuss services and next steps.",
  },
];

export default function Refer() {
  const [f, setF] = useState({
    referrer_name: "",
    referrer_email: "",
    referrer_phone: "",
    individual_name: "",
    relationship: "",
    notes: "",
  });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/referrals", f);
      toast.success("Referral submitted. Our team will reach out within one business day.");
      setF({
        referrer_name: "",
        referrer_email: "",
        referrer_phone: "",
        individual_name: "",
        relationship: "",
        notes: "",
      });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="bg-brand-cream" data-testid="refer-page">
      <Toaster richColors position="top-right" />

      <div className="wc-container pt-12 md:pt-16 pb-14 md:pb-20">
        {/* Intro */}
        <div className="max-w-3xl" data-testid="refer-intro">
          <div className="inline-flex items-center gap-2.5 text-[10px] md:text-[11px] font-bold uppercase tracking-[0.28em] text-brand-navy" data-testid="refer-eyebrow">
            <span className="h-px w-8 bg-brand-orange" />
            Refer Someone
          </div>
          <h1
            className="mt-4 font-outfit font-semibold tracking-tight text-brand-navy leading-[1.06] text-[2rem] md:text-[2.5rem] lg:text-[2.75rem]"
            data-testid="refer-title"
          >
            Start a referral. We&apos;ll take it{" "}
            <span
              className="italic font-medium text-brand-orange relative whitespace-nowrap"
              style={{ fontFamily: "'DM Serif Display', Georgia, serif" }}
            >
              from here
              <svg
                className="absolute -bottom-1.5 left-0 w-full"
                viewBox="0 0 220 12"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <path d="M 4 8 C 60 2, 130 12, 216 4" stroke="#f39325" strokeWidth="3" fill="none" strokeLinecap="round" />
              </svg>
            </span>
            <span className="text-brand-navy">.</span>
          </h1>
          <p className="mt-4 text-slate-600 text-[15px] md:text-base leading-relaxed max-w-2xl" data-testid="refer-subtitle">
            This form is for support coordinators, case managers, family members, guardians, and individuals seeking
            services. Our intake team responds within one business day.
          </p>
        </div>

        {/* Two-column: form (left) + What happens next (right) */}
        <div className="mt-10 md:mt-12 grid grid-cols-1 lg:grid-cols-[65fr_35fr] gap-6 lg:gap-8 items-start">
          {/* LEFT — Referral form */}
          <form
            onSubmit={submit}
            className="bg-white rounded-3xl border border-brand-stone/60 p-6 md:p-8 shadow-[0_12px_32px_rgba(15,26,74,0.06)] space-y-5"
            data-testid="refer-form"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Your Name" required>
                <input
                  required
                  type="text"
                  value={f.referrer_name}
                  onChange={set("referrer_name")}
                  data-testid="refer-name"
                  className={inputClass}
                />
              </FormField>
              <FormField label="Your Email" required>
                <input
                  required
                  type="email"
                  value={f.referrer_email}
                  onChange={set("referrer_email")}
                  data-testid="refer-email"
                  className={inputClass}
                />
              </FormField>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Your Phone">
                <input
                  type="tel"
                  value={f.referrer_phone}
                  onChange={set("referrer_phone")}
                  data-testid="refer-phone"
                  className={inputClass}
                />
              </FormField>
              <FormField label="Your Role / Relationship to Individual" required>
                <select
                  required
                  value={f.relationship}
                  onChange={set("relationship")}
                  data-testid="refer-relationship"
                  className={`${inputClass} bg-white`}
                >
                  <option value="" disabled>
                    Select one
                  </option>
                  {ROLE_OPTIONS.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>

            <FormField label="Individual's Name" required>
              <input
                required
                type="text"
                value={f.individual_name}
                onChange={set("individual_name")}
                data-testid="refer-individual"
                className={inputClass}
              />
            </FormField>

            <FormField label="Notes">
              <textarea
                rows={5}
                value={f.notes}
                onChange={(e) => setF({ ...f, notes: e.target.value })}
                data-testid="refer-notes"
                className={inputClass}
              />
            </FormField>

            <button
              type="submit"
              disabled={sending}
              data-testid="refer-submit-btn"
              className="inline-flex items-center gap-2 rounded-full bg-brand-navy text-white hover:bg-brand-navy/90 disabled:opacity-60 px-8 py-3.5 text-xs font-bold uppercase tracking-[0.22em] shadow-sm"
              style={{ transition: "background-color 200ms ease" }}
            >
              {sending ? "Submitting…" : (<>Submit Referral <ArrowRight className="w-4 h-4" /></>)}
            </button>
          </form>

          {/* RIGHT — What happens next panel */}
          <aside
            className="rounded-3xl bg-brand-navy text-white p-6 md:p-7 shadow-[0_18px_40px_rgba(15,26,74,0.18)] relative overflow-hidden"
            data-testid="refer-next-panel"
          >
            <svg className="absolute -top-16 -right-16 w-56 h-56 pointer-events-none opacity-60" viewBox="0 0 200 200" aria-hidden="true">
              <circle cx="100" cy="100" r="100" fill="#f39325" fillOpacity="0.15" />
            </svg>

            <div className="relative">
              <div className="inline-flex items-center gap-2.5 text-[10px] font-bold uppercase tracking-[0.28em] text-brand-orange">
                <span className="h-px w-6 bg-brand-orange" />
                Next Steps
              </div>
              <h2 className="mt-3 font-outfit text-2xl md:text-[26px] font-semibold tracking-tight leading-tight">
                What happens next?
              </h2>
              <p className="mt-2 text-white/75 text-[13.5px] leading-relaxed">
                We make getting started simple.
              </p>

              <ol className="mt-6 space-y-5">
                {STEPS.map(({ n, title, body }) => (
                  <li key={n} className="flex gap-3.5" data-testid={`refer-step-${n}`}>
                    <span className="font-outfit text-brand-orange font-bold text-[13px] tracking-widest shrink-0 pt-0.5">
                      {n}
                    </span>
                    <div>
                      <div className="font-outfit text-[15px] font-semibold text-white leading-snug">{title}</div>
                      <p className="mt-1 text-[13px] text-white/70 leading-relaxed">{body}</p>
                    </div>
                  </li>
                ))}
              </ol>

              <div className="mt-7 pt-5 border-t border-white/15">
                <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/60">
                  Prefer to talk with someone?
                </div>
                <a
                  href="tel:7579519555"
                  className="mt-2 inline-flex items-center gap-2 font-outfit text-[19px] font-semibold text-white hover:text-brand-orange"
                  style={{ transition: "color 200ms ease" }}
                  data-testid="refer-phone-cta"
                >
                  <Phone className="w-4 h-4 text-brand-orange" strokeWidth={2} />
                  757-951-9555
                </a>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

const inputClass =
  "mt-2 w-full rounded-xl border border-brand-stone/70 bg-white px-4 py-3 text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-brand-navy focus:ring-1 focus:ring-brand-navy/20";

function FormField({ label, required, children }) {
  return (
    <div>
      <label className="text-[11px] font-bold uppercase tracking-[0.22em] text-slate-500">
        {label}
        {required && <span className="text-brand-orange ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}
